import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge } from '@/components/ui';
import { useQa } from './hooks/useQa';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { QaTable } from './components/QaTable';
import { QaFormModal } from './components/QaFormModal';
import type { QaInspection, QaStatus } from './types/maintenance-qa.types';

const statusLabels: Partial<Record<QaStatus, string>> = {
  draft: 'Bản nháp',
  pending_inspection: 'Chờ kiểm tra',
  inspecting: 'Đang kiểm tra',
  passed: 'Đạt chất lượng',
  failed: 'Không đạt',
  rework_required: 'Yêu cầu sửa lại',
  rework_done: 'Đã sửa xong',
  closed: 'Đã đóng',
  cancelled: 'Đã hủy',
};

export default function MaintenanceQaPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();
  
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState<QaInspection | null>(null);

  const {
    inspections,
    total,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useQa(companyId);

  return (
    <PageLayout
      title="Kiểm soát chất lượng (QA)"
      description="Giám sát chất lượng bảo dưỡng hiện trường, chấm điểm và quản lý các yêu cầu sửa chữa (Rework)."
      breadcrumbs={[{ label: 'Bảo dưỡng' }, { label: 'QA' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-settings-line">Quản lý Checklist</Button>
          <AuthButton 
            module="maintenance" 
            action="create" 
            variant="primary" 
            icon="ri-add-line" 
            onClick={() => setShowCreate(true)}
          >
            Tạo đợt kiểm tra
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Chờ kiểm tra"
            value={stats?.pendingInspections || 0}
            icon="ri-time-line"
            variant="info"
          />
          <KPICard
            title="Đã xong hôm nay"
            value={stats?.completedToday || 0}
            icon="ri-checkbox-circle-line"
            variant="success"
          />
          <KPICard
            title="Điểm trung bình"
            value={stats?.averageScore?.toFixed(1) || '0.0'}
            icon="ri-star-line"
            variant="warning"
            suffix="/10"
          />
          <KPICard
            title="Tỉ lệ đạt"
            value={Math.round(stats?.passRate || 0)}
            icon="ri-shield-check-line"
            variant="primary"
            suffix="%"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm mã đợt kiểm tra..."
                value={filters.search}
                onChange={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <QaTable 
            data={inspections} 
            isLoading={isLoading} 
            onEdit={(item) => setEditItem(item)}
            onRowClick={(item) => navigate(`./detail/${item.id}`)} 
          />
        </Card>
      </div>

      <QaFormModal
        isOpen={showCreate || !!editItem}
        item={editItem}
        companyId={companyId}
        onClose={() => {
          setShowCreate(false);
          setEditItem(null);
        }}
      />
    </PageLayout>
  );
}
